"use client"

import type { IconButtonProps, SpanProps } from "@chakra-ui/react"
import type { ThemeProviderProps } from "next-themes"

import { ClientOnly, IconButton, Skeleton, Span } from "@chakra-ui/react"
import { ThemeProvider, useTheme } from "next-themes"
import * as React from "react"
import { AutoThemeIcon, MoonIcon, SunIcon } from "@/components/icons"

export interface ColorModeProviderProps extends ThemeProviderProps {}

export function ColorModeProvider(props: ColorModeProviderProps) {
  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      disableTransitionOnChange
      enableColorScheme
      enableSystem
      {...props}
    />
  )
}

export type ColorMode = "light" | "dark"
export type ColorModeSetting = ColorMode | "system"

export interface UseColorModeReturn {
  colorMode: ColorMode
  colorModeSetting: ColorModeSetting
  setColorMode: (colorMode: ColorModeSetting) => void
  toggleColorMode: () => void
}

const colorModeCycle: ColorModeSetting[] = ["system", "light", "dark"]

function getColorModeSetting(theme?: string): ColorModeSetting {
  return theme === "light" || theme === "dark" || theme === "system"
    ? theme
    : "system"
}

export function useColorMode(): UseColorModeReturn {
  const { forcedTheme, resolvedTheme, setTheme, theme } = useTheme()
  const colorMode = forcedTheme || resolvedTheme || "light"
  const colorModeSetting = getColorModeSetting(forcedTheme || theme)
  const toggleColorMode = () => {
    const currentIndex = colorModeCycle.indexOf(colorModeSetting)
    const nextIndex = (currentIndex + 1) % colorModeCycle.length

    setTheme(colorModeCycle[nextIndex] ?? "system")
  }
  return {
    colorMode: colorMode as ColorMode,
    colorModeSetting,
    setColorMode: setTheme,
    toggleColorMode,
  }
}

export function useColorModeValue<T>(light: T, dark: T) {
  const { colorMode } = useColorMode()
  return colorMode === "dark" ? dark : light
}

export function ColorModeIcon() {
  const { colorMode, colorModeSetting } = useColorMode()
  if (colorModeSetting === "system") {
    return <AutoThemeIcon />
  }

  return colorMode === "dark" ? <MoonIcon /> : <SunIcon />
}

interface ColorModeButtonProps extends Omit<IconButtonProps, "aria-label"> {}

export const ColorModeButton = React.forwardRef<
  HTMLButtonElement,
  ColorModeButtonProps
>(function ColorModeButton(props, ref) {
  const { toggleColorMode } = useColorMode()
  return (
    <ClientOnly fallback={<Skeleton boxSize="9" />}>
      <IconButton
        aria-label="Toggle color mode"
        onClick={toggleColorMode}
        ref={ref}
        size="sm"
        variant="ghost"
        {...props}
        css={{
          _icon: {
            height: "5",
            width: "5",
          },
        }}
      >
        <ColorModeIcon />
      </IconButton>
    </ClientOnly>
  )
})

export const LightMode = React.forwardRef<HTMLSpanElement, SpanProps>(
  function LightMode(props, ref) {
    return (
      <Span
        className="chakra-theme light"
        color="fg"
        colorPalette="gray"
        colorScheme="light"
        display="contents"
        ref={ref}
        {...props}
      />
    )
  }
)

export const DarkMode = React.forwardRef<HTMLSpanElement, SpanProps>(
  function DarkMode(props, ref) {
    return (
      <Span
        className="chakra-theme dark"
        color="fg"
        colorPalette="gray"
        colorScheme="dark"
        display="contents"
        ref={ref}
        {...props}
      />
    )
  }
)
